import React from 'react';
import ReactDatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { clsx } from 'clsx';
import pt from 'date-fns/locale/pt-BR';

interface DatePickerProps {
  label?: string;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  value?: Date | string | null;
  onChange?: (date: Date | null) => void;
  className?: string;
  minDate?: Date;
  maxDate?: Date;
  filterDate?: (date: Date) => boolean;
}

const DatePicker: React.FC<DatePickerProps> = ({
  label,
  error,
  required = false,
  disabled = false,
  placeholder,
  value,
  onChange,
  className,
  minDate,
  maxDate,
  filterDate,
}) => {
  const baseClasses = 'block w-full px-3 py-2 border rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:border-transparent sm:text-sm';
  const defaultClasses = 'border-gray-300 focus:ring-primary-500 focus:border-primary-500';
  const errorClasses = error ? 'border-error-500 focus:ring-error-500 focus:border-error-500' : '';
  
  const inputClasses = clsx(
    baseClasses,
    defaultClasses,
    errorClasses,
    className
  );

  // Converter string para Date se necessário (usando fuso horário local)
  const dateValue = value ? (typeof value === 'string' ? (() => {
    // Verificar se a string tem timezone (formato ISO)
    if (value.includes('T')) {
      const date = new Date(value);
      // Verificar se a data é válida
      return isNaN(date.getTime()) ? null : date;
    }
    // Formato simples YYYY-MM-DD
    const [year, month, day] = value.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return isNaN(date.getTime()) ? null : date;
  })() : value) : null;

  // Função para verificar se é fim de semana
  const isWeekend = (date: Date) => {
    const day = date.getDay();
    return day === 0 || day === 6;
  };

  // Função para customizar o render dos dias
  const renderDayContents = (dayOfMonth: number, date?: Date) => {
    if (date && isWeekend(date)) {
      return (
        <span style={{ color: '#dc2626', fontWeight: 'bold' }}>
          {dayOfMonth}
        </span>
      );
    }
    return dayOfMonth;
  };

  return (
    <div className="w-full">
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
          {required && <span className="text-error-500 ml-1">*</span>}
        </label>
      )}
      <ReactDatePicker
        selected={dateValue}
        onChange={onChange}
        locale={pt}
        dateFormat="dd/MM/yyyy"
        placeholderText={placeholder || 'Selecione uma data'}
        className={inputClasses}
        disabled={disabled}
        minDate={minDate}
        maxDate={maxDate}
        filterDate={filterDate}
        calendarClassName="custom-calendar"
        renderDayContents={renderDayContents}
      />
      {error && (
        <p className="mt-1 text-sm text-error-600">
          {error}
        </p>
      )}
    </div>
  );
};

export default DatePicker;
